import { Box, Button, Typography, Stack } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HistoryIcon from '@mui/icons-material/History';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { brand } from '../theme/tokens';

interface ClassicWrapperProps {
  /** Path of the revamped version to jump back to. */
  newPath: string;
  children: ReactNode;
}

/**
 * Wraps an ORIGINAL (untouched) page with a sticky banner announcing that
 * this is the classic design, plus a button back to the refreshed version.
 * Lets the owner compare old vs new without changing any legacy page code.
 */
const ClassicWrapper = ({ newPath, children }: ClassicWrapperProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        justifyContent="center"
        flexWrap="wrap"
        useFlexGap
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 3,
          px: 2,
          py: 1,
          background: brand.ink,
          color: '#fff',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <HistoryIcon fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {t('versionToggle.classicBanner', "You're viewing the classic version")}
          </Typography>
        </Stack>
        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={<AutoAwesomeIcon />}
          onClick={() => navigate(newPath)}
        >
          {t('versionToggle.switchToNew', 'Switch to new design')}
        </Button>
      </Stack>
      {children}
    </Box>
  );
};

export default ClassicWrapper;
